/**
 * Firestore Service
 * 
 * Handles database operations using Firebase Firestore.
 * Provides CRUD operations for various collections.
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

export type CollectionName = 'users' | 'pdfs' | 'clients' | 'newsletter_subscribers' | 'newsletters' | 'news' | 'sectors' | 'contact_messages' | 'comunicados';

export interface FirestoreDocument {
  id: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateResult {
  success: boolean;
  id?: string;
  error?: string;
}

export interface ReadResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ListResult<T> {
  success: boolean;
  data?: T[];
  error?: string;
}

export interface OperationResult {
  success: boolean;
  error?: string;
}

/**
 * Get a document by ID
 */
export async function getDocument<T extends FirestoreDocument>(
  collectionName: CollectionName,
  id: string
): Promise<ReadResult<T>> {
  try {
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        success: true,
        data: { id: docSnap.id, ...docSnap.data() } as T,
      };
    } else {
      return { success: false, error: 'Documento no encontrado.' };
    }
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

/**
 * Create a new document
 */
export async function createDocument<T extends Record<string, any>>(
  collectionName: CollectionName,
  data: T
): Promise<CreateResult> {
  try {
    const timestamp = new Date();
    
    // Filter out undefined properties to prevent Firestore from throwing on unsupported field value: undefined
    const cleanData = Object.entries(data).reduce((acc, [key, val]) => {
      if (val !== undefined) {
        acc[key] = val;
      }
      return acc;
    }, {} as Record<string, any>);

    const docData = {
      ...cleanData,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const docRef = await addDoc(collection(db, collectionName), docData);

    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('Error creating document:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update an existing document
 */
export async function updateDocument<T extends Record<string, any>>(
  collectionName: CollectionName,
  id: string,
  data: Partial<T>
): Promise<OperationResult> {
  try {
    const docRef = doc(db, collectionName, id);
    
    // Filter out undefined properties to prevent Firestore from throwing on unsupported field value: undefined
    const cleanData = Object.entries(data).reduce((acc, [key, val]) => {
      if (val !== undefined) {
        acc[key] = val;
      }
      return acc;
    }, {} as Record<string, any>);

    await updateDoc(docRef, {
      ...cleanData,
      updatedAt: new Date(),
    });

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

/**
 * Delete a document
 */
export async function deleteDocument(
  collectionName: CollectionName,
  id: string
): Promise<OperationResult> {
  try {
    const docRef = doc(db, collectionName, id);
    await deleteDoc(docRef);

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

/**
 * List documents with optional query constraints
 */
export async function listDocuments<T extends FirestoreDocument>(
  collectionName: CollectionName,
  constraints?: QueryConstraint[]
): Promise<ListResult<T>> {
  try {
    let q = query(collection(db, collectionName));

    if (constraints && constraints.length > 0) {
      q = query(collection(db, collectionName), ...constraints);
    }

    const querySnapshot = await getDocs(q);
    const documents: T[] = [];

    querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
      documents.push({ id: doc.id, ...doc.data() } as T);
    });

    return { success: true, data: documents };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

/**
 * Get all documents in a collection
 */
export async function getAllDocuments<T extends FirestoreDocument>(
  collectionName: CollectionName
): Promise<ListResult<T>> {
  return listDocuments<T>(collectionName);
}

/**
 * Get all documents in a collection (alias for getAllDocuments)
 */
export async function getCollection<T extends FirestoreDocument>(
  collectionName: CollectionName
): Promise<ListResult<T>> {
  return listDocuments<T>(collectionName);
}

/**
 * Query documents by a single field
 */
export async function queryByField<T extends FirestoreDocument>(
  collectionName: CollectionName,
  fieldName: string,
  value: any
): Promise<ListResult<T>> {
  return listDocuments<T>(collectionName, [where(fieldName, '==', value)]);
}

/**
 * Query documents with custom constraints
 */
export async function queryCollection<T extends FirestoreDocument>(
  collectionName: CollectionName,
  constraints: QueryConstraint[]
): Promise<ListResult<T>> {
  return listDocuments<T>(collectionName, constraints);
}

/**
 * Query documents by category (useful for PDFs)
 */
export async function queryByCategory<T extends FirestoreDocument>(
  collectionName: CollectionName,
  categoryField: string,
  category: string
): Promise<ListResult<T>> {
  return listDocuments<T>(collectionName, [
    where(categoryField, '==', category),
    orderBy('createdAt', 'desc'),
  ]);
}

/**
 * Get documents with pagination
 */
export async function getDocumentsPaginated<T extends FirestoreDocument>(
  collectionName: CollectionName,
  _page: number = 1,
  pageSize: number = 10,
  constraints?: QueryConstraint[]
): Promise<ListResult<T>> {
  const constraintsWithPagination: QueryConstraint[] = [
    ...(constraints || []),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  ];

  return listDocuments<T>(collectionName, constraintsWithPagination);
}

/**
 * Count documents in a collection
 */
export async function countDocuments(
  collectionName: CollectionName,
  constraints?: QueryConstraint[]
): Promise<{ success: boolean; count?: number; error?: string }> {
  try {
    let q = query(collection(db, collectionName));

    if (constraints && constraints.length > 0) {
      q = query(collection(db, collectionName), ...constraints);
    }

    const snapshot = await getDocs(q);
    return { success: true, count: snapshot.size };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

/**
 * Check if a document exists
 */
export async function documentExists(
  collectionName: CollectionName,
  id: string
): Promise<boolean> {
  const result = await getDocument(collectionName, id);
  return result.success && result.data !== undefined;
}

/**
 * Date converter for Firestore timestamps
 */
export const dateConverter = {
  toFirestore(data: any): any {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): any {
    const data = snapshot.data(options);
    // Convert Firestore timestamps to JavaScript dates
    Object.keys(data).forEach((key) => {
      if (data[key]?.toDate) {
        data[key] = data[key].toDate();
      }
    });
    return data;
  },
};

/**
 * Create a document reference
 */
export function getDocumentRef(collectionName: CollectionName, id: string) {
  return doc(db, collectionName, id);
}

/**
 * Create a collection reference
 */
export function getCollectionRef(collectionName: CollectionName) {
  return collection(db, collectionName);
}

// Default export for backward compatibility
const firestoreService = {
  getDocument,
  createDocument,
  updateDocument,
  deleteDocument,
  listDocuments,
  getAllDocuments,
  getCollection,
  queryByField,
  queryCollection,
  queryByCategory,
  getDocumentsPaginated,
  countDocuments,
  documentExists,
  getDocumentRef,
  getCollectionRef,
};

export default firestoreService;